import React, { createContext, useContext, useState, useEffect } from 'react';

import { API_BASE } from '../utils/apiBase';
import { logger } from '../../utils/logger';

const AuthContext = createContext(null);

let _clientPortalToken = '';

export const getClientPortalToken = () =>
  _clientPortalToken ||
  localStorage.getItem('client_portal_token') ||
  localStorage.getItem('vendor_portal_token') ||
  '';

export const setClientPortalToken = (token) => {
  _clientPortalToken = token || '';
  try {
    if (token) {
      localStorage.setItem('client_portal_token', token);
      localStorage.setItem('vendor_portal_token', token);
    } else {
      localStorage.removeItem('client_portal_token');
      localStorage.removeItem('vendor_portal_token');
    }
  } catch {
    /* ignore */
  }
};

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(() => getClientPortalToken());
  const [user, setUser] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [activeContext, setActiveContext] = useState(null);
  const [loading, setLoading] = useState(Boolean(getClientPortalToken()));

  useEffect(() => {
    _clientPortalToken = accessToken;
    try {
      if (accessToken) {
        localStorage.setItem('client_portal_token', accessToken);
        localStorage.setItem('vendor_portal_token', accessToken);
      } else {
        localStorage.removeItem('client_portal_token');
        localStorage.removeItem('vendor_portal_token');
      }
    } catch {
      /* ignore */
    }
    if (accessToken) {
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, [accessToken]);

  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/client-portal/me`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (res.ok) {
        const json = await res.json();
        setUser(json.user);
        setOrganizations(json.organizations || []);
        setContexts(json.contexts || []);
        setActiveContext(json.active_context || null);
      } else if (res.status === 401) {
        // Genuine 401 Unauthenticated token -> clear token
        setAccessToken('');
        setClientPortalToken('');
      } else {
        // 403 or other context error -> preserve token session
        logger.warn("Client profile fetch returned non-200 status", res.status);
      }
    } catch (err) {
      logger.error("Failed to fetch user info", err?.message || "User fetch error");
    } finally {
      setLoading(false);
    }
  };

  const login = (token) => {
    setClientPortalToken(token);
    setAccessToken(token);
  };

  const logout = () => {
    const currentToken = accessToken || getClientPortalToken();
    if (currentToken) {
      fetch(`${API_BASE}/api/client-portal/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      }).catch(() => {});
    }
    setClientPortalToken('');
    setAccessToken('');
    setUser(null);
    setOrganizations([]);
    setContexts([]);
    setActiveContext(null);
  };

  return (
    <AuthContext.Provider value={{ accessToken, user, organizations, contexts, activeContext, loading, login, logout, isAuthenticated: !!accessToken }}>
      {children}
    </AuthContext.Provider>
  );
};


export const useAuth = () => useContext(AuthContext) || {};

